import * as React from 'react';
import {useEffect, useState} from 'react';
import {IonIcon, IonSpinner, useIonAlert} from '@ionic/react';
import {chatboxEllipsesOutline, checkmarkCircleOutline, documentAttachOutline,
        refreshOutline, trashBinSharp} from 'ionicons/icons';

import Panel from '../ui/Panel';
import firebaseService from '../../services/firebaseService';
import {issueLabels} from '../../assets/issueLabels';
import './ReportedIssues.css';

interface Issue {
    id: string;
    title?: string;
    type?: string;
    organization?: string;
    requesterName?: string;
    requesterEmail?: string;
    requestedDate?: string;
    description?: string;
    labels?: string[];
    attachmentUrl?: string;
    attachmentFileName?: string;
    /** Absent on everything filed before this existed, which reads as pending. */
    status?: 'pending' | 'resolved';
}

type Filter = 'pending' | 'resolved' | 'all';

const isResolved = (issue: Issue) => issue.status === 'resolved';

const LABEL_COLOR: Record<string, string> = Object.fromEntries(
    issueLabels.map(label => [label.name, label.color]));

/** "Wed Sep 02 2026 2:19:07 PM" -> a number to sort by, 0 when unparseable. */
function reportedAt(issue: Issue): number {
    const parsed = Date.parse(issue.requestedDate ?? '');
    return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Support requests, for admins. Anyone can file one; only an admin has reason
 * to read the pile, so the page renders this panel for them alone.
 */
const ReportedIssues: React.FC = () => {
    const [issues, setIssues] = useState<Issue[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openId, setOpenId] = useState('');
    const [busyId, setBusyId] = useState('');
    // Pending first: the reason to open this panel is the work not yet done.
    const [filter, setFilter] = useState<Filter>('pending');
    const [confirmDelete] = useIonAlert();

    useEffect(() => {
        const unsubscribe = firebaseService.getFeedbackRequests().onSnapshot(
            docs => {
                const found: Issue[] = [];
                // Spread first: a document that happens to carry its own `id`
                // field must not shadow the one Firestore filed it under.
                docs.forEach(doc => found.push({...(doc.data() as Issue), id: doc.id}));
                found.sort((a, b) => reportedAt(b) - reportedAt(a));
                setIssues(found);
                setLoading(false);
            },
            err => {
                setError(err.message);
                setLoading(false);
            });
        return () => unsubscribe();
    }, []);

    const shown = issues.filter(issue =>
        filter === 'all' || (filter === 'resolved' ? isResolved(issue) : !isResolved(issue)));
    const pendingCount = issues.filter(issue => !isResolved(issue)).length;

    const setStatus = (issue: Issue, status: 'pending' | 'resolved') => {
        setBusyId(issue.id);
        firebaseService.setFeedbackStatus(issue.id, status)
            .catch(err => setError(err.message))
            .finally(() => setBusyId(''));
    };

    const remove = (issue: Issue) => {
        confirmDelete({
            header: 'Delete this report?',
            message: 'The report and its attachment are removed for good. This cannot be undone.',
            buttons: [
                'Cancel',
                {
                    text: 'Delete',
                    handler: () => {
                        setBusyId(issue.id);
                        // The screenshot goes with the report; a file nothing
                        // points at is just storage nobody will ever look in.
                        const attachment = issue.attachmentUrl
                            ? firebaseService.deleteAttachment(issue.attachmentUrl).catch(() => undefined)
                            : Promise.resolve();
                        attachment
                            .then(() => firebaseService.deleteFeedbackRequest(issue.id))
                            .catch(err => setError(err.message))
                            .finally(() => setBusyId(''));
                    },
                },
            ],
        });
    };

    if (loading) {
        return (
            <Panel title="Reported issues">
                <div className="issues-empty"><IonSpinner /></div>
            </Panel>
        );
    }

    return (
        <Panel
            title="Reported issues"
            subtitle={error
                ? 'Could not load the reports'
                : `${pendingCount} pending of ${issues.length}, newest first`}
            accent="var(--domain-clearance)"
            icon={<IonIcon icon={chatboxEllipsesOutline} />}
        >
            {error && <p className="issues-empty">{error}</p>}

            <div className="issues-filter" role="group" aria-label="Filter reports">
                {(['pending', 'resolved', 'all'] as Filter[]).map(key => (
                    <button
                        type="button"
                        key={key}
                        className={filter === key ? 'issues-filter__btn issues-filter__btn--on' : 'issues-filter__btn'}
                        onClick={() => setFilter(key)}
                    >
                        {key === 'pending' ? `Pending (${pendingCount})`
                            : key === 'resolved' ? `Resolved (${issues.length - pendingCount})`
                            : `All (${issues.length})`}
                    </button>
                ))}
            </div>

            {!error && shown.length === 0 && (
                <p className="issues-empty">
                    {issues.length === 0
                        ? 'Nobody has reported anything yet.'
                        : `Nothing ${filter === 'resolved' ? 'resolved' : 'pending'}.`}
                </p>
            )}

            <ul className="issues">
                {shown.map(issue => {
                    const open = openId === issue.id;
                    return (
                        <li className="issue" key={issue.id}>
                            <button
                                type="button"
                                className="issue__head"
                                aria-expanded={open}
                                onClick={() => setOpenId(open ? '' : issue.id)}
                            >
                                <span className="issue__title">
                                    {issue.title || issue.type || 'Support request'}
                                    {isResolved(issue) && (
                                        <span className="issue__status">Resolved</span>
                                    )}
                                </span>
                                <span className="issue__meta">
                                    {issue.requesterName || issue.requesterEmail}
                                    {issue.requestedDate && ` · ${issue.requestedDate}`}
                                </span>
                            </button>

                            {(issue.labels ?? []).length > 0 && (
                                <div className="issue__labels">
                                    {(issue.labels ?? []).map(name => (
                                        <span
                                            className="issue__label"
                                            key={name}
                                            style={{['--label-color' as string]:
                                                LABEL_COLOR[name] ?? 'var(--border-hairline)'} as React.CSSProperties}
                                        >
                                            {name}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {open && (
                                <div className="issue__body">
                                    <dl className="issue__facts">
                                        <div><dt>Area</dt><dd>{issue.type || '-'}</dd></div>
                                        <div><dt>Organization</dt><dd>{issue.organization || '-'}</dd></div>
                                        <div><dt>Email</dt><dd>{issue.requesterEmail || '-'}</dd></div>
                                    </dl>
                                    <p className="issue__description">{issue.description}</p>
                                    {issue.attachmentUrl && (
                                        <a className="issue__attachment" href={issue.attachmentUrl}
                                           target="_blank" rel="noreferrer">
                                            <IonIcon icon={documentAttachOutline} aria-hidden="true" />
                                            {issue.attachmentFileName || 'Attachment'}
                                        </a>
                                    )}

                                    <div className="issue__actions">
                                        <button
                                            type="button"
                                            className="issue__action"
                                            disabled={busyId === issue.id}
                                            onClick={() => setStatus(issue, isResolved(issue) ? 'pending' : 'resolved')}
                                        >
                                            <IonIcon icon={isResolved(issue) ? refreshOutline : checkmarkCircleOutline}
                                                     aria-hidden="true" />
                                            {isResolved(issue) ? 'Move back to pending' : 'Mark resolved'}
                                        </button>
                                        <button
                                            type="button"
                                            className="issue__action issue__action--danger"
                                            disabled={busyId === issue.id}
                                            onClick={() => remove(issue)}
                                        >
                                            <IonIcon icon={trashBinSharp} aria-hidden="true" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </Panel>
    );
};

export default ReportedIssues;
